import { ReactNode } from 'react';
import { useLogger } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ApiError } from '@packages/shared/core/common/errors';
import {
  EmailFormHelpers,
  EmailModalFormHelpers,
  EmailWithDateStepData,
  QuizScreenProps,
} from '@packages/shared/core/quiz/entities';

type EmailWithDateQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const EmailWithDateQuizStep = ({ id, renderQuizScreen }: EmailWithDateQuizStepProps) => {
  const logger = useLogger();

  const {
    back,
    next,
    stepData,
    userPreferences,
    signUpUser,
    updateUserPreferences,
    updateUserInteraction,
    totalSteps,
  } = useQuizStep(id);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { dataKey } = stepData as EmailWithDateStepData;

  const goBack = async () => back();

  const initialValue = typeof userPreferences[dataKey] === 'string' ? (userPreferences[dataKey] as string) : '';

  const onDateSelected = async (date: string) => {
    updateUserPreferences('date', date);
    updateUserInteraction(dataKey);
  };

  const handleSubmit = async (email: string, { setErrors }: EmailFormHelpers | EmailModalFormHelpers) => {
    try {
      await signUpUser({ email });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ email: err.originalError.message });
      } else {
        setErrors({ email: 'Something went wrong. Try again later' });
      }
      return;
    }

    await next();
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        initialValue,
        onChange: onDateSelected,
        onSubmit: handleSubmit,
        totalSteps,
        ...(stepData as EmailWithDateStepData),
      })}
    </>
  );
};

export default EmailWithDateQuizStep;
